import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';
import './SetFees.css';
import './FeeRecords.css';

function FeeRecords() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [feeRecords, setFeeRecords] = useState({}); // {studentId: {isPaid: boolean, paymentDate: string, notes: string}}
  const [feePeriod, setFeePeriod] = useState(''); // e.g., "2025-01" for January 2025
  const [saving, setSaving] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchClasses();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    setSelectedClassId('');
    setSelectedSectionId('');
    setStudents([]);
    setFeeRecords({});
  }, [selectedBranchId]);

  useEffect(() => {
    if (schoolData?.id && selectedClassId) {
      fetchSections();
    } else {
      setSections([]);
    }
  }, [schoolData?.id, selectedClassId]);

  useEffect(() => {
    if (schoolData?.id && selectedClassId && selectedSectionId) {
      fetchStudents();
      fetchFeeRecords();
    } else {
      setStudents([]);
      setFeeRecords({});
    }
  }, [schoolData?.id, selectedClassId, selectedSectionId, feePeriod]);

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('class_name', { ascending: true });

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchSections = async () => {
    if (!selectedClassId) {
      setSections([]);
      setLoadingSections(false);
      return;
    }
    
    setLoadingSections(true);
    try {
      // Get sections from the classes table (sections stored as JSON array)
      const { data: classData, error: classError } = await supabase
        .from('classes')
        .select('sections')
        .eq('id', selectedClassId)
        .single();
      
      console.log('Class data:', classData, 'Error:', classError);
      
      if (classError) {
        console.error('Error fetching class sections:', classError);
        // Fallback: Get sections from students
        let studentsQuery = supabase
          .from('students')
          .select('section_id')
          .eq('school_id', schoolData.id)
          .eq('class_id', selectedClassId)
          .not('section_id', 'is', null);

        if (selectedBranchId != null) {
          studentsQuery = studentsQuery.eq('branch_id', selectedBranchId);
        }

        const { data: studentsData, error: studentsError } = await studentsQuery;

        console.log('Students data for sections:', studentsData, 'Error:', studentsError);

        if (!studentsError && studentsData && studentsData.length > 0) {
          const uniqueSectionIds = [...new Set(studentsData.map(s => s.section_id))].sort();
          const sectionObjects = uniqueSectionIds.map((sectionId) => ({
            id: sectionId,
            section_name: typeof sectionId === 'string' ? sectionId : `Section ${sectionId}`,
            class_id: selectedClassId
          }));
          console.log('Sections from students:', sectionObjects);
          setSections(sectionObjects);
        } else {
          setSections([]);
        }
        setLoadingSections(false);
        return;
      }
      
      if (classData && classData.sections && Array.isArray(classData.sections) && classData.sections.length > 0) {
        // Sections are stored as an array in the classes table
        const sectionObjects = classData.sections
          .filter(section => section && section.trim() !== '')
          .map((section) => ({
            id: section,
            section_name: section,
            class_id: selectedClassId
          }));
        console.log('Sections from class:', sectionObjects);
        setSections(sectionObjects);
      } else {
        // If no sections in class, try getting from students
        let studentsQueryFallback = supabase
          .from('students')
          .select('section_id')
          .eq('school_id', schoolData.id)
          .eq('class_id', selectedClassId)
          .not('section_id', 'is', null);

        if (selectedBranchId != null) {
          studentsQueryFallback = studentsQueryFallback.eq('branch_id', selectedBranchId);
        }

        const { data: studentsData, error: studentsError } = await studentsQueryFallback;

        console.log('Fallback - Students data for sections:', studentsData, 'Error:', studentsError);

        if (!studentsError && studentsData && studentsData.length > 0) {
          const uniqueSectionIds = [...new Set(studentsData.map(s => s.section_id))].sort();
          const sectionObjects = uniqueSectionIds.map((sectionId) => ({
            id: sectionId,
            section_name: typeof sectionId === 'string' ? sectionId : `Section ${sectionId}`,
            class_id: selectedClassId
          }));
          console.log('Sections from students (fallback):', sectionObjects);
          setSections(sectionObjects);
        } else {
          setSections([]);
        }
      }
    } catch (err) {
      console.error('Error fetching sections:', err);
      setSections([]);
    } finally {
      setLoadingSections(false);
    }
  };

  const fetchStudents = async () => {
    setLoadingStudents(true);
    try {
      let query = supabase
        .from('students')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClassId)
        .eq('section_id', selectedSectionId)
        .eq('is_active', true)
        .order('first_name', { ascending: true });

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching students:', error);
        return;
      }

      setStudents(data || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchFeeRecords = async () => {
    if (!feePeriod) return;
    
    try {
      let query = supabase
        .from('fee_records')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClassId)
        .eq('section_id', selectedSectionId)
        .eq('fee_period', feePeriod);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching fee records:', error);
        return;
      }

      // Convert array to object for easy lookup
      const recordsObj = {};
      (data || []).forEach(record => {
        recordsObj[record.student_id] = {
          isPaid: record.is_paid,
          paymentDate: record.payment_date || '',
          notes: record.notes || '',
          recordId: record.id
        };
      });
      setFeeRecords(recordsObj);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handlePaymentToggle = (studentId, isPaid) => {
    setFeeRecords(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        isPaid: isPaid,
        paymentDate: isPaid ? new Date().toISOString().split('T')[0] : ''
      }
    }));
  };

  const handlePaymentDateChange = (studentId, date) => {
    setFeeRecords(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        paymentDate: date
      }
    }));
  };

  const handleNotesChange = (studentId, notes) => {
    setFeeRecords(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        notes: notes
      }
    }));
  };

  const handleSaveRecords = async () => {
    if (!feePeriod) {
      alert('Please select a fee period first.');
      return;
    }

    if (students.length === 0) {
      alert('No students to save records for.');
      return;
    }

    setSaving(true);
    try {
      const recordsToSave = students.map(student => {
        const record = feeRecords[student.id] || { isPaid: false, paymentDate: '', notes: '' };
        return {
          school_id: schoolData.id,
          class_id: selectedClassId,
          section_id: selectedSectionId,
          branch_id: selectedBranchId,
          student_id: student.id,
          fee_period: feePeriod,
          is_paid: record.isPaid || false,
          payment_date: record.paymentDate || null,
          notes: record.notes || null,
          updated_at: new Date().toISOString()
        };
      });

      // Use upsert to insert or update records
      const { error } = await supabase
        .from('fee_records')
        .upsert(recordsToSave, {
          onConflict: 'school_id,class_id,section_id,student_id,fee_period',
          ignoreDuplicates: false
        });

      if (error) {
        console.error('Error saving fee records:', error);
        alert('Failed to save fee records. Please try again.');
        return;
      }

      // Show success popup
      setShowSuccessPopup(true);
      fetchFeeRecords(); // Refresh to get updated IDs
      
      // Auto-close popup after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to save fee records. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  // Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  // Initialize fee period to current month
  useEffect(() => {
    if (!feePeriod) {
      setFeePeriod(getCurrentMonth());
    }
  }, []);

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>💰 Fee Records</h1>
          <p>Track fee payment status for students</p>
        </div>
        <div className="branch-filter-row">
          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />
          <span className="branch-context-text">
            {selectedBranchId
              ? 'Filtering fee records by selected branch'
              : 'Showing fee records for all branches'}
          </span>
        </div>

        <div className="fee-records-container">
          <div className="section-card">
            <h2>📋 Fee Payment Records</h2>
            <p className="section-description">
              Select a class and section to view and update fee payment status for students.
            </p>

            <div className="form-row" style={{ marginBottom: '2rem' }}>
              <div className="form-group">
                <label htmlFor="fee-period">Fee Period *</label>
                <input
                  type="month"
                  id="fee-period"
                  value={feePeriod}
                  onChange={(e) => setFeePeriod(e.target.value)}
                  className="form-input"
                  required
                />
                <p style={{ fontSize: '0.85rem', color: '#6B7280', marginTop: '0.5rem' }}>
                  Select the month/period for which you want to record fees
                </p>
              </div>
            </div>

            <div className="form-row" style={{ marginBottom: '2rem' }}>
              <div className="form-group">
                <label htmlFor="class-select">Select Class *</label>
                <select
                  id="class-select"
                  value={selectedClassId}
                  onChange={(e) => {
                    setSelectedClassId(e.target.value);
                    setSelectedSectionId('');
                    setStudents([]);
                    setFeeRecords({});
                  }}
                  className="form-select"
                  required
                >
                  <option value="">Choose a class...</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.class_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="section-select">Select Section *</label>
                <select
                  id="section-select"
                  value={selectedSectionId}
                  onChange={(e) => {
                    setSelectedSectionId(e.target.value);
                    setStudents([]);
                    setFeeRecords({});
                  }}
                  className="form-select"
                  required
                  disabled={!selectedClassId}
                >
                  <option value="">Choose a section...</option>
                  {loadingSections ? (
                    <option value="" disabled>Loading sections...</option>
                  ) : sections.length === 0 && selectedClassId ? (
                    <option value="" disabled>No sections found</option>
                  ) : (
                    sections.map((section) => (
                      <option key={section.id || section} value={section.id || section}>
                        {section.section_name || section.name || section}
                      </option>
                    ))
                  )}
                </select>
                {loadingSections && selectedClassId && (
                  <p style={{ fontSize: '0.85rem', color: '#6B7280', marginTop: '0.5rem' }}>
                    Loading sections...
                  </p>
                )}
                {!loadingSections && sections.length === 0 && selectedClassId && (
                  <p style={{ fontSize: '0.85rem', color: '#EF4444', marginTop: '0.5rem' }}>
                    No sections found for this class. Please add sections to the class first.
                  </p>
                )}
                {!loadingSections && sections.length > 0 && (
                  <p style={{ fontSize: '0.85rem', color: '#059669', marginTop: '0.5rem' }}>
                    {sections.length} section{sections.length !== 1 ? 's' : ''} found
                  </p>
                )}
              </div>
            </div>

            {loadingStudents ? (
              <div className="loading-container" style={{ padding: '2rem' }}>
                <div className="loading-spinner"></div>
                <p>Loading students...</p>
              </div>
            ) : students.length > 0 ? (
              <>
                <div className="fee-records-students-header">
                  <h3>Students ({students.length})</h3>
                  <button
                    onClick={handleSaveRecords}
                    className="btn-primary"
                    disabled={saving}
                    style={{ minWidth: '150px' }}
                  >
                    {saving ? '💾 Saving...' : '💾 Save Records'}
                  </button>
                </div>

                <div className="fee-records-table-container">
                  <div className="fee-records-table-wrapper">
                    <div className="fee-records-table-header">
                      <div className="fee-records-header-cell">Student Name</div>
                      <div className="fee-records-header-cell">Roll Number</div>
                      <div className="fee-records-header-cell center">Payment Status</div>
                      <div className="fee-records-header-cell center">Payment Date</div>
                      <div className="fee-records-header-cell">Notes</div>
                  </div>
                  {students.map((student, index) => {
                    const record = feeRecords[student.id] || { isPaid: false, paymentDate: '', notes: '' };
                    return (
                      <div 
                        key={student.id} 
                          className={`fee-records-table-row ${index % 2 === 0 ? 'even' : 'odd'}`}
                        >
                          <div className="fee-records-table-cell">
                            <div className="fee-records-student-name">
                            {student.first_name} {student.middle_name} {student.last_name}
                          </div>
                        </div>
                          <div className="fee-records-table-cell">
                            <div className="fee-records-roll-number">
                            {student.student_id_no || 'N/A'}
                          </div>
                        </div>
                          <div className="fee-records-table-cell center">
                            <label className="fee-records-payment-status">
                            <input
                              type="checkbox"
                              checked={record.isPaid}
                              onChange={(e) => handlePaymentToggle(student.id, e.target.checked)}
                                className="fee-records-checkbox"
                            />
                              <span className={`fee-records-status-badge ${record.isPaid ? 'paid' : 'unpaid'}`}>
                              {record.isPaid ? 'Paid' : 'Unpaid'}
                            </span>
                          </label>
                        </div>
                          <div className="fee-records-table-cell center">
                          <input
                            type="date"
                            value={record.paymentDate}
                            onChange={(e) => handlePaymentDateChange(student.id, e.target.value)}
                            disabled={!record.isPaid}
                              className="fee-records-date-input"
                          />
                        </div>
                          <div className="fee-records-table-cell">
                          <input
                            type="text"
                            value={record.notes}
                            onChange={(e) => handleNotesChange(student.id, e.target.value)}
                            placeholder="Optional notes..."
                              className="fee-records-notes-input"
                          />
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>

                <div className="fee-records-summary">
                  <div className="fee-records-summary-content">
                    <div>
                      <div className="fee-records-summary-text">Summary:</div>
                      <div className="fee-records-summary-stats">
                        Total Students: {students.length} | 
                        Paid: {Object.values(feeRecords).filter(r => r.isPaid).length} | 
                        Unpaid: {students.length - Object.values(feeRecords).filter(r => r.isPaid).length}
                      </div>
                    </div>
                    <button
                      onClick={handleSaveRecords}
                      className="btn-primary"
                      disabled={saving}
                    >
                      {saving ? '💾 Saving...' : '💾 Save All Records'}
                    </button>
                  </div>
                </div>
              </>
            ) : selectedClassId && selectedSectionId ? (
              <div className="no-fees">
                <p>No students found in this class and section.</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="success-popup-overlay">
          <div className="success-popup">
            <div className="success-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="success-popup-title">Success!</h3>
            <p className="success-popup-message">
              Fee records saved successfully!
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeeRecords;

